import * as React from 'react';

import i18n from 'helper/i18n';
import noop from 'helper/noop';

import Cta, { CtaProps } from 'components/atoms/Cta';
import CtaRect from 'components/atoms/CtaRect';
import Flag from 'components/atoms/Flag';
import IconNumber from 'components/atoms/IconNumber';
import Image from 'components/atoms/Image';
import Info from 'components/atoms/Info';
import Intensity from 'components/atoms/Intensity';
import Bar from 'components/molecules/Bar';
import BarItem from 'components/molecules/BarItem';
import Notice from 'components/molecules/Notice';
import Overlay from 'components/molecules/Overlay';

import { IconNumberProps } from 'components/atoms/IconNumber';
import { ImageProps } from 'components/atoms/Image';
import { ContainsType } from 'components/views/ProgramPreview';

export type ProgramStageProps = {
	title?: string;
	topic?: string;
	roof?: string;
	image?: ImageProps;
	intensity: number;
	userIntensity: number;
	contains?: ContainsType;
	duration?: string;
	inactive?: boolean;
	contentTypes: IconNumberProps[];
	cta?: Partial<CtaProps> | CtaProps;
	isRecommended?: boolean;
	isNewProgram?: boolean;
	isLoadingProgram?: boolean;
	onStartProgram: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export const mightAddPlural = (unit: string, duration: number) => {
	switch (unit) {
		case 'day':
			return `${unit}${duration === 1 ? '' : 's'}`;
		case 'week':
			return `${unit}${duration % 7 === 0 && duration > 14 ? '' : 's'}`;
		default:
			return unit;
	}
};

export const getDurationLabel = (unit: string, duration: number) => {
	unit = mightAddPlural(unit, duration);
	return i18n(`react.impulsCoach.components.ProgramStage.durationUnits.${unit}`);
};

export class ProgramStage extends React.Component<ProgramStageProps> {
	public static defaultProps = {
		onStartProgram: noop,
		intensity: 0,
		userIntensity: null,
		contentTypes: [],
		cta: {
			text: i18n('react.impulsCoach.components.ProgramStage.ctaCaption'),
		} as CtaProps,
		isLoadingProgram: false,
		isNewProgram: false,
	};

	public getDurationCaption(duration: number) {
		let label = '';

		if (duration && duration > 0) {
			if (duration < 7) {
				label = getDurationLabel('day', duration);
			} else if (duration >= 7 && duration % 7 === 0) {
				label = getDurationLabel('day', duration);
			} else {
				label = getDurationLabel('day', duration);
			}
		}

		return `${duration} ${label}`;
	}

	public renderContentTypes(contentTypes: IconNumberProps[]): (JSX.Element | void)[] | void {
		if (contentTypes && contentTypes.length > 0) {
			return contentTypes.map((item: IconNumberProps, index: number) => {
				if (item.type && (item.count || 0) >= 0) {
					item.label = i18n(
						`react.impulsCoach.components.ProgramStage.contains.${item.type}Caption${
							item.count === 1 ? '' : 'Plural'
						}`
					);

					return (
						<li key={index} className="m-program-stage__types-item">
							<IconNumber type={item.type} count={item.count} label={item.label} text={item.text} />
						</li>
					);
				}

				return void 0;
			});
		}
	}

	public renderIntensity() {
		const { intensity, userIntensity } = this.props;

		// 0 = personalized
		if (intensity === 0) {
			// logged in?
			if (userIntensity) {
				// user intensity
				return (
					<Intensity
						modifier="scaled"
						value={userIntensity}
						label={i18n('react.impulsCoach.components.ProgramStage.intensityCaption')}
					/>
				);
			}

			return (
				<Info
					modifier="scaled"
					label={i18n('react.impulsCoach.components.ProgramStage.intensityCaption')}
					text={i18n(`react.impulsCoach.components.ProgramStage.personalizedText`)}
				/>
			);
		}

		// show intensity
		return (
			<Intensity
				modifier="scaled"
				value={intensity}
				label={i18n('react.impulsCoach.components.ProgramStage.intensityCaption')}
			/>
		);
	}

	public render() {
		const {
			title,
			roof,
			image,
			contentTypes,
			cta,
			contains,
			inactive,
			onStartProgram,
			isRecommended,
			isNewProgram,
			isLoadingProgram,
		} = this.props;

		return (
			<div className="m-program-stage">
				<div className="m-program-stage__head">
					<div className="m-program-stage__head-inner">
						<div className="container container--full-width-ms">
							<div className="row">
								<div className="col-xs-12 col-sm-5">
									<div className="m-program-stage__image-wrap">
										<div className="m-program-stage__image">
											<Image {...image!} />
										</div>
										<div className="m-program-stage__bg" />
									</div>
								</div>
								<div className="col-xs-12 col-sm-7">
									<div className="m-program-stage__info">
										<span className="m-program-stage__goal">{roof}</span>
										<h1 className="m-program-stage__title">{title}</h1>
										<div className="m-program-stage__bar">
											<Bar>
												<BarItem>
													<Info
														modifier="scaled"
														label={i18n(
															'react.impulsCoach.components.ProgramStage.durationCaption'
														)}
														text={
															this.getDurationCaption(
																contains && contains.days ? contains.days : 0
															) || ''
														}
													/>
												</BarItem>
												<BarItem>{this.renderIntensity()}</BarItem>
											</Bar>
										</div>
										<div className="m-program-stage__button">
											{!inactive && (
												<CtaRect
													loading={isLoadingProgram}
													delegate={onStartProgram}
													text={cta!.text!}
												/>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className="row">
								<div className="col-xs-12">
									<div className="m-program-stage__button-sm-up">
										{!inactive && (
											<Cta
												loading={isLoadingProgram}
												delegate={onStartProgram}
												icon="play"
												text={i18n(
													'react.impulsCoach.components.ProgramContents.startCtaCaption'
												)}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{(isRecommended || isNewProgram) && (
						<div className="m-program-stage__flag-wrap">
							{isNewProgram && (
								<div className="m-program-stage__flag-tip">
									<Flag
										text={i18n('react.impulsCoach.components.ProgramStage.isNewProgramRoof')}
										modifier={['scaled', 'yellow']}
									/>
								</div>
							)}
							{isRecommended && (
								<div className="m-program-stage__flag">
									<Flag
										text={i18n('react.impulsCoach.components.ProgramStage.recommendedRoof')}
										modifier="scaled"
									/>
								</div>
							)}
						</div>
					)}
				</div>

				<div className="m-program-stage__types">
					<div className="container">
						<div className="row">
							<div className="col-xs-12">
								<ul className="m-program-stage__types-list">
									{this.renderContentTypes(contentTypes) as JSX.Element[]}
								</ul>
							</div>
						</div>
					</div>
				</div>

				<Overlay isOpen={!!isLoadingProgram} closable={false} closeButton={false} animation="zoom">
					<Notice text={i18n('react.impulsCoach.components.ProgramStage.startProgramLoaderText')} />
				</Overlay>
			</div>
		);
	}
}

export default ProgramStage;
